// useChat — owns conversation state for the chat surface.
//
// Returns:
//   threads / loadingThreads / refreshThreads()
//   active / setActive(id)
//   messages / sending / thinking / send(text)
//   error
//
// Wraps the RPC + SSE plumbing so the UI components stay declarative.
// Streaming model:
//   - send() calls api.runStart with the user message + prior conversation
//   - SSE listener appends `token` event text into the most-recent
//     assistant message (creating one on first token)
//   - `agent:done` flips `thinking` off and refreshes the thread list
//     (so updatedAt and messageCount stay current)
//
// Approvals + ask_user events surface as `pending` so the UI can render
// a prompt. The hook exposes `respondToApproval` and `respondToAsk` to
// answer them.

import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { api, type Session, type SessionMessage } from "./rpc";
import { sse, type AgentEvent } from "./sse";

export interface Attachment {
  kind: "image";
  name: string;
  dataUrl: string;
  mime: string;
  size: number;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** Inline image URLs (data: or http) attached to a user turn. */
  images?: string[];
}

// Discriminated row in the rendered message stream. "msg" is a normal
// user/assistant bubble; "tools" is a persisted tool-call group
// extracted from an assistant turn's `tool_calls` + the matching
// `role: "tool"` result messages that follow; "reasoning" is the
// model's chain-of-thought (delta.reasoning / reasoning_content)
// captured during streaming and persisted on the assistant row.
export type ChatItem =
  | { kind: "msg"; msg: ChatMessage }
  | { kind: "tools"; id: string; calls: ToolCallView[] }
  | { kind: "reasoning"; id: string; text: string; running: boolean };

export interface PendingApproval {
  approvalId: string;
  toolName: string;
  toolArgs: unknown;
  dangerous?: boolean;
  dangerousReason?: string;
}

export interface PendingQuestion {
  questionId: string;
  question: string;
  options?: { label: string; description?: string }[];
}

/** Marker inserted into the scrollback when the LLM context is compacted.
 *  Anchored to the message index just before the next outgoing turn so
 *  the user sees a horizontal-rule-style note above any messages sent
 *  after the compaction. The visible chat doesn't shrink — only what
 *  goes to the LLM does. */
export interface CompactionMarker {
  /** Index in the messages array where this marker should appear. */
  atIndex: number;
  /** LLM-context message count before / after the compaction. */
  before: number;
  after: number;
}

/** Live view of a tool the agent is calling this turn. */
export interface ToolCallView {
  id: string;
  name: string;
  args: unknown;
  /** `null` until the tool_result arrives. */
  result: string | null;
  denied?: boolean;
  error?: string;
}

function mintSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for very old contexts. mirror-agent's isValidSessionId is
  // forgiving enough to accept any reasonably-shaped string here.
  return "s-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

function flattenContent(content: SessionMessage["content"]): { text: string; images: string[] } {
  if (typeof content === "string") return { text: content, images: [] };
  if (Array.isArray(content)) {
    const text = content
      .map((p) => (p.type === "text" ? p.text || "" : ""))
      .filter(Boolean)
      .join("\n");
    const images = content
      .filter((p) => p.type === "image_url" && p.image_url?.url)
      .map((p) => p.image_url!.url);
    return { text, images };
  }
  return { text: "", images: [] };
}

function toChatMessage(m: SessionMessage, i: number): ChatMessage | null {
  if (m.role !== "user" && m.role !== "assistant") return null; // skip system / tool roles in the view
  const { text, images } = flattenContent(m.content);
  // Assistant messages that only carry tool_calls sometimes serialize
  // their content as whitespace ("\n\n") rather than "". Drop them
  // here so the chat-message stream stays in lock-step with
  // buildChatItems, which already filters the same way at line 169.
  // Without this, persistedMsgCount (from items) ends up one shy of
  // messages.length and the "live overflow" render path duplicates
  // the next legitimate assistant bubble — the classic "two identical
  // replies stacked" symptom.
  if (m.role === "assistant" && !text.trim() && images.length === 0) return null;
  // Include the array index alongside _seq so duplicate _seq values
  // (server-side glitch we sometimes see when two user messages get
  // the same monotonic sequence number) don't collide as Preact keys.
  return {
    id: `${i}-${m._seq ?? "n"}-${m.role}`,
    role: m.role,
    text,
    images: images.length > 0 ? images : undefined,
  };
}

// Build the renderable item list from raw SessionMessages. Walks the
// conversation once, emitting:
//  • a "msg" item for every user/assistant message that has content
//  • a "tools" item for every assistant turn that carried tool_calls,
//    pre-joined with the matching role:"tool" result messages so the
//    group renders post-refresh exactly as it did live.
//
// This is what lets a tool-call sequence survive a page reload. The
// raw SessionMessages already carry tool_calls (assistant.tool_calls)
// and results (tool.tool_call_id + tool.content) — we just hadn't
// been surfacing them in the UI.
function buildChatItems(raw: SessionMessage[]): ChatItem[] {
  // First pass: index tool-result messages by their tool_call_id so
  // the second pass can look them up O(1).
  const resultByCallId = new Map<string, SessionMessage>();
  for (const m of raw) {
    if (m.role === "tool" && m.tool_call_id) {
      resultByCallId.set(m.tool_call_id, m);
    }
  }
  const items: ChatItem[] = [];
  raw.forEach((m, i) => {
    // Keep assistant text first, with reasoning rendered afterward in
    // its own row for easier scanability while preserving turn grouping.
    if (m.role === "assistant" && typeof m.reasoning_content === "string"
        && m.reasoning_content.length > 0) {
      items.push({
        kind: "reasoning",
        id: `reasoning-${i}-${m._seq ?? "n"}`,
        text: m.reasoning_content,
        running: false,
      });
    }
    if (m.role === "user" || m.role === "assistant") {
      const cm = toChatMessage(m, i);
      // Trim whitespace before checking — assistant messages that only
      // carried tool_calls sometimes serialize their `content` as
      // "\n\n\n" rather than "" (LLM-dependent). Whitespace-only
      // strings are truthy in JS but represent "no visible text", so
      // a raw `cm.text` check would emit a phantom msg row that
      // breaks the merge run across iterations.
      if (cm && (cm.text.trim() || cm.images?.length)) items.push({ kind: "msg", msg: cm });
    }
    if (m.role === "assistant" && m.tool_calls && m.tool_calls.length > 0) {
      const calls: ToolCallView[] = m.tool_calls.map((tc) => {
        let args: unknown = {};
        try { args = JSON.parse(tc.function.arguments); }
        catch { args = tc.function.arguments; } // fall back to raw string
        const result = resultByCallId.get(tc.id);
        const resultText = result
          ? (typeof result.content === "string"
              ? result.content
              : flattenContent(result.content).text)
          : null;
        return {
          id: tc.id,
          name: tc.function.name,
          args,
          result: resultText,
        };
      });
      items.push({ kind: "tools", id: `tools-${i}-${m._seq ?? "n"}`, calls });
    }
  });
  return mergeAcrossTurns(items);
}

// Per-turn merge: collapse every reasoning + tools item in a turn
// into a single Thinking row and a single tools-summary row at the
// TOP of the turn, then drop all assistant text bubbles underneath.
// A "turn" runs from one user message to the next (or to the end of
// the conversation).
//
// Why this layout instead of strictly chronological interleaving:
// the live UI already shows the activity as a single growing
// Thinking row + single growing tools row (the streaming side has
// no way to slot calls back in line with text bubbles). Keeping the
// persisted view matching that shape removes the jarring "snap"
// where the screen rearranged once agent:done fired. The cost is
// that you no longer see exactly which text bubble each call
// happened between — for that, click the tools row to inspect
// individual calls, or the reasoning row for the full chain.
function mergeAcrossTurns(items: ChatItem[]): ChatItem[] {
  const out: ChatItem[] = [];
  let pendingReasoning: { id: string; parts: string[] } | null = null;
  let pendingTools: { id: string; calls: ToolCallView[] } | null = null;
  let pendingAssistantBubbles: ChatItem[] = [];
  const flushTurn = () => {
    for (const b of pendingAssistantBubbles) out.push(b);
    pendingAssistantBubbles = [];
    if (pendingReasoning) {
      out.push({
        kind: "reasoning",
        id: pendingReasoning.id,
        text: pendingReasoning.parts.join("\n\n— — —\n\n"),
        running: false,
      });
      pendingReasoning = null;
    }
    if (pendingTools) {
      out.push({ kind: "tools", id: pendingTools.id, calls: pendingTools.calls });
      pendingTools = null;
    }
  };
  for (const item of items) {
    if (item.kind === "msg" && item.msg.role === "user") {
      // User message marks the start of a new turn — flush whatever
      // accumulated for the previous turn first.
      flushTurn();
      out.push(item);
    } else if (item.kind === "msg") {
      // Assistant bubble: hold until end of turn so it renders BELOW
      // the merged reasoning + tools rows.
      pendingAssistantBubbles.push(item);
    } else if (item.kind === "reasoning") {
      if (!pendingReasoning) pendingReasoning = { id: item.id, parts: [] };
      pendingReasoning.parts.push(item.text);
    } else if (item.kind === "tools") {
      if (!pendingTools) pendingTools = { id: item.id, calls: [] };
      pendingTools.calls.push(...item.calls);
    }
  }
  flushTurn();
  return out;
}

export interface UseChat {
  threads: Session[];
  loadingThreads: boolean;
  refreshThreads: () => Promise<void>;
  active: string | null;
  setActive: (id: string | null) => void;
  newThread: () => void;
  messages: ChatMessage[];
  /** Interleaved render list: user/assistant bubbles + persisted
   *  tool-call groups, in conversation order. Survives reload because
   *  it's rebuilt from the raw SessionMessage history. */
  items: ChatItem[];
  loadingMessages: boolean;
  sending: boolean;
  thinking: boolean;
  send: (text: string, mode: string, attachments?: Attachment[]) => Promise<void>;
  clearLocal: () => void;
  /** Permanently delete a thread + its conversation history (calls
   *  session.delete on the server). Clears the active view if it
   *  was the one being deleted. */
  deleteThread: (id: string) => Promise<void>;
  pendingApproval: PendingApproval | null;
  respondToApproval: (approved: boolean, reason?: string) => Promise<void>;
  pendingQuestion: PendingQuestion | null;
  respondToAsk: (answer: string) => Promise<void>;
  /** Live tool calls + results for the currently-streaming turn. Cleared on send and on thread switch. */
  toolCalls: ToolCallView[];
  /** Live reasoning text from the currently-streaming turn. The
   *  agent_loop accumulates it onto the assistant message before
   *  persistence, so the persisted form re-appears via `items` on
   *  reload — this state is just for the in-flight render. */
  liveReasoning: string;
  /** Compaction markers — anchored to a message index in the visible scrollback. */
  compactionMarkers: CompactionMarker[];
  /** True while the compaction LLM call is in flight. Disable composer, show a status row. */
  compacting: boolean;
  /** Active prompt preset — applied as systemPromptAppend on the next send. */
  activePreset: { name: string; body: string } | null;
  setActivePreset: (p: { name: string; body: string } | null) => void;
  /** Server-side conversation compaction: summarises older LLM-context turns. UI scrollback stays intact; only rawRef shrinks. */
  compact: () => Promise<{ before: number; after: number } | null>;
  /** Build a markdown export of the current thread. Caller arranges the download. */
  exportMarkdown: () => string;
  error: string | null;
}

export function useChat(): UseChat {
  const [threads, setThreads] = useState<Session[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [active, setActive] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [items, setItems] = useState<ChatItem[]>([]);
  // Live reasoning text accumulated from `type: "reasoning"` SSE events
  // during a streaming turn. Cleared on send (new turn = new reasoning)
  // and on agent:done after items has absorbed the persisted version.
  const [liveReasoning, setLiveReasoning] = useState<string>("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [pendingApproval, setPendingApproval] = useState<PendingApproval | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<PendingQuestion | null>(null);
  const [toolCalls, setToolCalls] = useState<ToolCallView[]>([]);
  const [compactionMarkers, setCompactionMarkers] = useState<CompactionMarker[]>([]);
  const [compacting, setCompacting] = useState(false);
  // Active preset persists across reloads via localStorage — once a
  // preset is applied it stays the default until the user clears it
  // or picks a different one.
  const [activePreset, _setActivePreset] = useState<{ name: string; body: string } | null>(() => {
    try {
      const raw = localStorage.getItem("hub.activePreset.v1");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const setActivePreset = useCallback((p: { name: string; body: string } | null) => {
    _setActivePreset(p);
    try {
      if (p) localStorage.setItem("hub.activePreset.v1", JSON.stringify(p));
      else localStorage.removeItem("hub.activePreset.v1");
    } catch {}
  }, []);
  // Re-fetch the saved preset body once on mount so an edit made
  // through the panel after the initial apply takes effect on the
  // next reload (the locally-cached body would otherwise drift).
  useEffect(() => {
    if (!activePreset) return;
    let cancelled = false;
    api.presetGet(activePreset.name)
      .then((p) => {
        if (cancelled || !p) return;
        if (p.body !== activePreset.body) setActivePreset({ name: p.name, body: p.body });
      })
      .catch(() => {
        // Preset deleted server-side → clear local cache.
        if (!cancelled) setActivePreset(null);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount, NOT on every activePreset change
  const [error, setError] = useState<string | null>(null);

  // Track the conversation as the server-shaped message array so we can
  // POST it back to run.start. The UI's ChatMessage list is a flattened
  // view of this; we keep the raw form so tool_calls / multimodal parts
  // don't get lost in round-trips.
  const rawRef = useRef<SessionMessage[]>([]);
  // Incremented when we make an optimistic local mutation (send/new token).
  // Active-thread loads capture a snapshot and skip applying if a newer local
  // mutation happened before the load resolved.
  const localMutationSeqRef = useRef(0);
  // Mirror of `active` for the SSE listener (which captures state at effect-
  // setup time and would otherwise see a stale null on the first turn of a
  // new thread). Kept in sync by the active-load effect below.
  const activeRef = useRef<string | null>(null);
  // Session id minted locally by send() for a brand-new thread. The
  // active-load effect skips its server reload for this one id so it
  // doesn't race the run's first save and wipe the optimistic user
  // message (intermittent "my sent message vanished", esp. on mobile
  // where the load resolves before the message is persisted).
  const mintedSessionRef = useRef<string | null>(null);

  const refreshThreads = useCallback(async () => {
    setLoadingThreads(true);
    try {
      const list = await api.sessionList();
      // Chat-kind sessions only — coding sessions live elsewhere in the
      // original app and aren't the right surface for the new Chat tab.
      const chatOnly = list.filter((s) => s.kind === "chat" || s.kind == null);
      setThreads(chatOnly);
      setError(null);
      // Pick first thread on initial load if nothing's selected.
      // Functional updater so a freshly-minted session (e.g. from a
      // pendingMessage send the Today QuickChat triggered) doesn't
      // get clobbered by this auto-select firing slightly later —
      // the closure'd `active` may still be null even after another
      // path has set it.
      if (chatOnly.length > 0) setActive((prev) => prev === null ? chatOnly[0].id : prev);
    } catch (e: any) {
      setError(e?.message || "failed to load conversations");
    } finally {
      setLoadingThreads(false);
    }
  }, [active]);

  // Initial load + SSE subscription.
  useEffect(() => { refreshThreads(); }, []);

  // Load messages whenever the active thread changes.
  useEffect(() => {
    activeRef.current = active;
    if (active === null) {
      setMessages([]);
      setItems([]);
      rawRef.current = [];
      setToolCalls([]);
      setCompactionMarkers([]);
      return;
    }
    // Freshly minted by send() on this very turn: the optimistic user
    // message is already in `messages` and rawRef is set. Reloading from
    // the server here would race the run's first save and blow that
    // message away. Skip the load — agent:done reconciles server-truth
    // into items/rawRef once the turn completes.
    if (active === mintedSessionRef.current) {
      mintedSessionRef.current = null;
      return;
    }
    let cancelled = false;
    const loadSeq = localMutationSeqRef.current;
    setLoadingMessages(true);
    setToolCalls([]);
    setCompactionMarkers([]);
    api.sessionLoad(active).then((r) => {
      if (cancelled) return;
      // Stale-load guard: don't clobber optimistic local turns (most visible
      // symptom: the just-sent user message disappears until run completion).
      if (activeRef.current === active && localMutationSeqRef.current !== loadSeq) {
        return;
      }
      rawRef.current = r.messages;
      const flat: ChatMessage[] = [];
      r.messages.forEach((m, i) => {
        const cm = toChatMessage(m, i);
        if (cm) flat.push(cm);
      });
      setMessages(flat);
      setItems(buildChatItems(r.messages));
    }).catch((e: any) => {
      if (!cancelled) setError(e?.message || "failed to load conversation");
    }).finally(() => {
      if (!cancelled) setLoadingMessages(false);
    });
    return () => { cancelled = true; };
  }, [active]);

  // SSE listener.
  useEffect(() => {
    return sse.on((ev: AgentEvent) => {
      if (ev.type === "token" && typeof (ev as any).text === "string") {
        const text = (ev as any).text as string;
        setThinking(false);
        setMessages((prev) => {
          // Append to the trailing assistant bubble or create a new one.
          if (prev.length > 0 && prev[prev.length - 1].role === "assistant") {
            const last = prev[prev.length - 1];
            return [...prev.slice(0, -1), { ...last, text: last.text + text }];
          }
          return [...prev, { id: `live-${Date.now()}`, role: "assistant", text }];
        });
        return;
      }
      if (ev.type === "agent:done") {
        setThinking(false);
        setSending(false);
        refreshThreads();
        // Reload server-truth into rawRef so the NEXT run.start carries the
        // assistant's reply (and any tool calls) it just made. Without this,
        // rawRef is frozen at "user just typed" and the model sees the next
        // turn as two consecutive user messages with no assistant in between
        // — i.e. it "forgets" the question it asked. The UI's flattened
        // `messages` list stays as-is (token stream already populated it),
        // so the user sees no flicker; only rawRef is reconciled.
        const sid = activeRef.current;
        if (sid) {
          api.sessionLoad(sid).then((r) => {
            if (activeRef.current === sid) {
              rawRef.current = r.messages;
              // Rebuild persisted-items so the just-finished turn's
              // tool calls show up in scrollback. Clearing live
              // toolCalls in the same tick swaps the on-screen group
              // from "live" to "persisted" with no double-render —
              // both lists hold the same calls; the persisted one
              // survives reload, the live one doesn't.
              setItems(buildChatItems(r.messages));
              setToolCalls([]);
              setLiveReasoning("");
            }
          }).catch(() => { /* best-effort */ });
        }
        return;
      }
      if (ev.type === "agent:approval") {
        const a = ev as any;
        setPendingApproval({
          approvalId: a.approvalId,
          toolName: a.toolName,
          toolArgs: a.toolArgs,
          dangerous: a.dangerous,
          dangerousReason: a.dangerousReason,
        });
        return;
      }
      if (ev.type === "agent:approval_expired") {
        setPendingApproval(null);
        return;
      }
      if (ev.type === "agent:ask_user") {
        const q = ev as any;
        setPendingQuestion({
          questionId: q.questionId,
          question: q.question,
          options: q.options,
        });
        return;
      }
      if (ev.type === "reasoning" && typeof (ev as any).text === "string") {
        const text = (ev as any).text as string;
        setLiveReasoning((prev) => prev + text);
        return;
      }
      if (ev.type === "tool_call") {
        const c = ev as any;
        setToolCalls((prev) => [
          ...prev,
          { id: c.id, name: c.name, args: c.args, result: null },
        ]);
        return;
      }
      if (ev.type === "tool_result") {
        const r = ev as any;
        setToolCalls((prev) =>
          prev.map((c) =>
            c.id === r.id
              ? { ...c, result: typeof r.result === "string" ? r.result : "", denied: !!r.denied, error: r.error || undefined }
              : c,
          ),
        );
        return;
      }
    });
  }, [refreshThreads]);

  const send = useCallback(async (text: string, mode: string, attachments: Attachment[] = []) => {
    const trimmed = text.trim();
    // Empty body is allowed only when the user attached an image-only turn.
    if (!trimmed && attachments.length === 0) return;

    const images = attachments.filter((a) => a.kind === "image").map((a) => a.dataUrl);

    const userMsg: ChatMessage = {
      id: `local-${Date.now()}`,
      role: "user",
      text: trimmed,
      images: images.length > 0 ? images : undefined,
    };
    localMutationSeqRef.current += 1;
    setMessages((prev) => [...prev, userMsg]);

    // Snapshot conversation BEFORE adding this turn — server appends
    // the user message itself, so double-sending would duplicate the
    // user turn in the LLM payload.
    const priorConversation = rawRef.current.slice();

    // Local conversation record: multimodal when images are present so
    // a refresh + reload renders the user's image attachment again.
    const localContent: SessionMessage["content"] = images.length > 0
      ? [
          ...(trimmed ? [{ type: "text" as const, text: trimmed }] : []),
          ...images.map((url) => ({ type: "image_url" as const, image_url: { url } })),
        ]
      : trimmed;
    rawRef.current = [
      ...rawRef.current,
      { role: "user", content: localContent } as SessionMessage,
    ];

    const sessionForRun = active ?? mintSessionId();
    if (!active) {
      // Mark this id so the active-load effect skips its reload (which
      // would otherwise race the first save and wipe `userMsg`).
      mintedSessionRef.current = sessionForRun;
      setActive(sessionForRun);
    }
    // Sync the ref eagerly so the SSE listener's agent:done handler can
    // reload from server-truth even on the very first turn of a brand-new
    // thread (where setActive hasn't propagated yet).
    activeRef.current = sessionForRun;
    // Clear last turn's tool-call cards — the current turn starts fresh.
    setToolCalls([]);
    setLiveReasoning("");
    setSending(true);
    setThinking(true);
    setError(null);
    try {
      // Send the API-shaped attachments (kind/dataUrl) — the server
      // reconstructs the multimodal content from userMessage +
      // attachments itself, so the conversation body stays text-only
      // on the wire.
      await api.runStart({
        userMessage: trimmed,
        attachments: attachments.length > 0
          ? attachments.map((a) => ({ kind: a.kind, dataUrl: a.dataUrl, name: a.name, mime: a.mime }))
          : undefined,
        mode,
        conversation: priorConversation,
        savedSessionId: sessionForRun,
        systemPromptAppend: activePreset?.body || undefined,
      });
    } catch (e: any) {
      setSending(false);
      setThinking(false);
      setError(e?.message || "send failed");
    }
  }, [active, activePreset]);

  const newThread = useCallback(() => {
    // The server creates the session on first save during a run.
    // For now, clear the local view and let the next send create one.
    setActive(null);
    setMessages([]);
    rawRef.current = [];
  }, []);

  const clearLocal = useCallback(() => {
    setMessages([]);
    rawRef.current = [];
  }, []);

  const deleteThread = useCallback(async (id: string) => {
    await api.sessionDelete(id);
    // If we just deleted the active thread, clear the view back to
    // empty so the user lands on the "new thread" state instead of
    // staring at a stale scrollback. Otherwise just refresh the list.
    if (activeRef.current === id) {
      setActive(null);
      setMessages([]);
      setItems([]);
      rawRef.current = [];
      setToolCalls([]);
      setLiveReasoning("");
    }
    await refreshThreads();
  }, [refreshThreads]);

  const respondToApproval = useCallback(async (approved: boolean, reason?: string) => {
    if (!pendingApproval) return;
    try {
      await api.runApprove(pendingApproval.approvalId, approved, reason);
    } finally {
      setPendingApproval(null);
    }
  }, [pendingApproval]);

  const respondToAsk = useCallback(async (answer: string) => {
    if (!pendingQuestion) return;
    try {
      await api.runAnswer(pendingQuestion.questionId, answer);
    } finally {
      setPendingQuestion(null);
    }
  }, [pendingQuestion]);

  const compact = useCallback(async () => {
    if (rawRef.current.length === 0 || compacting) return null;
    setCompacting(true);
    setError(null);
    try {
      const r = await api.conversationCompact(active ?? undefined, rawRef.current);
      // Update the LLM-context buffer only — the user-visible scrollback
      // stays intact. Drop a marker at the end of the current messages
      // so the user sees a horizontal-rule-style note showing where the
      // context was truncated. Any new turn from this point forward
      // sends the compacted rawRef.
      rawRef.current = r.messages;
      setMessages((prev) => {
        setCompactionMarkers((markers) => [
          ...markers,
          { atIndex: prev.length, before: r.before, after: r.after },
        ]);
        return prev;
      });
      return { before: r.before, after: r.after };
    } catch (e: any) {
      setError(e?.message || "compact failed");
      return null;
    } finally {
      setCompacting(false);
    }
  }, [active, compacting]);

  const exportMarkdown = useCallback(() => {
    const lines: string[] = [];
    const thread = threads.find((t) => t.id === active);
    if (thread?.name) lines.push(`# ${thread.name}`, "");
    for (const m of messages) {
      const who = m.role === "user" ? "**You**" : "**Vulture Chat**";
      lines.push(`${who}:`, "", m.text || "_(image only)_");
      if (m.images && m.images.length > 0) {
        for (const url of m.images) lines.push(`![image](${url.slice(0, 80)}${url.length > 80 ? "…" : ""})`);
      }
      lines.push("", "---", "");
    }
    return lines.join("\n");
  }, [messages, threads, active]);

  return {
    threads, loadingThreads, refreshThreads,
    active, setActive,
    newThread,
    messages, items, loadingMessages, sending, thinking, send, clearLocal, deleteThread,
    pendingApproval, respondToApproval,
    pendingQuestion, respondToAsk,
    toolCalls,
    liveReasoning,
    compactionMarkers,
    compacting,
    activePreset, setActivePreset,
    compact, exportMarkdown,
    error,
  };
}
